import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { styled, keyframes } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Typography from '@mui/material/Typography';

import { dateFormat } from '@bloomays-lib/utils.shared';
import { Button, Title, createLoadingShimmerStyles } from '@bloomays-lib/ui.shared';
import { ApolloGqlJobFunnel, JobType } from '@bloomays-lib/types.shared';

import confettiSvg from '../../assets/illustrations/confetti.svg';
import { useTalentBoardStore } from '../../stores/talentBoardStore';

export type JobCardProps =
  | {
      data: Pick<ApolloGqlJobFunnel, 'name' | 'id' | 'expectedStartDate' | 'jobType' | 'status'>;
      loading: false;
    }
  | {
      data: null;
      loading: true;
    };

const JobCard = ({ data, loading }: JobCardProps) => {
  const { name, id, expectedStartDate, jobType, status } = loading
    ? { name: '...', id: '...', expectedStartDate: '...', jobType: '...', status: '...' }
    : data;

  const navigate = useNavigate();
  const { setNavState } = useTalentBoardStore();
  const { t } = useTranslation(['talentBoard', 'mission', 'jobs']);

  const getJobTypeLabel = (jobType: JobType): string => {
    switch (jobType) {
      case JobType.CDI:
      case JobType.CDI_Tech_Digital:
      case JobType.CDI_Bloomays_People:
        return 'CDI';
      case JobType.Freelance:
      case JobType.Freelance_Tech_Digital:
      case JobType.Freelance_Bloomays_People:
      case JobType.Freelance_RPO:
        return 'Freelance';
      case JobType.RPO:
        return 'RPO';
      case JobType.People:
        return 'People';
    }
  };

  return (
    <Container loading={loading}>
      {!loading && status && <Confetti src={confettiSvg} alt="" aria-hidden />}
      <Title
        variant="heading8"
        textTitle={name}
        sx={{ textAlign: 'left', lineHeight: '28px', padding: '0', width: '90%' }}
      />
      <Wrapper>
        {expectedStartDate && (
          <InfoRow>
            <InfoText variant="body1Light">{t('mission:beginMission', { ns: 'mission' })}</InfoText>
            <InfoText variant="body2SemiBold">
              {expectedStartDate !== '...' ? dateFormat(expectedStartDate, 'dd/MM/yyyy') : expectedStartDate}
            </InfoText>
          </InfoRow>
        )}

        {jobType && (
          <InfoRow>
            <InfoText variant="body1Light">{t('jobs:jobType', { ns: 'jobs' })}</InfoText>
            <InfoText variant="body2SemiBold">{!loading ? getJobTypeLabel(jobType as JobType) : jobType}</InfoText>
          </InfoRow>
        )}

        <InfoRow>
          <InfoText variant="body1Light">{t('jobs:statusLabel', { ns: 'jobs' })}:</InfoText>
          <InfoText variant="body2SemiBold">
            {loading
              ? status
              : status
              ? t(`jobs:statusWin`, { ns: 'jobs' })
              : t('jobs:statusInProgress', { ns: 'jobs' })}
          </InfoText>
        </InfoRow>

        <ButtonContainer>
          <Button
            textButton="Voir les candidats"
            emoji={<ArrowForwardIcon />}
            color="secondary"
            size="medium"
            onClick={() => {
              setNavState('talentboard');
              void navigate(`/talentboard/jobs/${id}`);
            }}
            disable={loading}
          />
        </ButtonContainer>
      </Wrapper>
    </Container>
  );
};

const Container = styled('div', { shouldForwardProp: (prop) => prop !== 'loading' })<{ loading: boolean }>(
  ({ theme, loading }) => ({
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '10px',
    borderRadius: '15px',
    width: '100%',
    minWidth: '280px',
    backgroundColor: theme.palette.neutral[10],
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    ...createLoadingShimmerStyles({ active: loading, contentOpacity: 0.6, speedMs: 1200, lightOpacity: 0.6 }),
  }),
);

const Wrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: '10px',
}));

const InfoRow = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '10px',
}));

const InfoText = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    lineHeight: '16.8px',
  },
}));

const ButtonContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignSelf: 'stretch',
}));

const heartbeat = keyframes`
  0%, 100% { transform: scale(1); }
  30% { transform: scale(1.30); }
  60% { transform: scale(1.14); }
`;

const Confetti = styled('img')(() => ({
  position: 'absolute',
  top: '15px',
  right: '15px',
  width: '32px',
  height: '32px',
  transformOrigin: 'center',
  animation: `${heartbeat} 2.6s ease-in-out infinite`,
  pointerEvents: 'none',
  userSelect: 'none',
}));

export default JobCard;
export { JobCard };
